import { memo } from 'react';
import { getSupplierThumb } from '@bridebook/web/lib/supplier/utils';
import { useSelector } from '@bridebook/web/app-basic/lib/utils';
import { useMarket } from '@bridebook/web/app-shared/lib/i18n/hooks/use-market';
import { getSupplierUrl } from '@bridebook/toolbox';
import { getLocalisedCanonical } from '@bridebook/web/lib/utils/i18n-routing/get-localised-canonical';
import {
  generateBreadcrumbListMicrodata,
  generateFaqPageMicrodata,
  generateItemListMicrodata,
  generateSearchResultItemMicrodata,
  generateWebPageMicrodata,
} from '@bridebook/web/lib/utils/microdata-schema-generators';
import {
  getActualCategory,
  getFAQContent,
  getSearchArea,
  getSearchList,
} from '@bridebook/web/app-basic/lib/search/selectors';
import { getBreadcrumbsLocation } from '@bridebook/web/app-shared/lib/utils';
import { useSearchMetaDesc } from '@bridebook/web/app-shared/lib/search/hooks/use-search-meta-desc';
import LdJsonScript from '@bridebook/web/app-shared/components/ld-json-script/ld-json-script';
import { ILocationData } from '@bridebook/web/app-shared/lib/search/types';

interface IProps {
  title: string;
  locationData: ILocationData | null;
}

const SearchResultsMicrodata = ({ title, locationData }: IProps) => {
  const market = useMarket();
  const list = useSelector(getSearchList);
  const faqs = useSelector(getFAQContent);
  const actualCategory = useSelector(getActualCategory);
  const area = useSelector(getSearchArea);

  const description = useSearchMetaDesc({ actualCategory, area, list, market });

  const searchItemsData = list.map((item) => {
    const logo = getSupplierThumb(item);
    const pathname = getSupplierUrl(item);
    const url = getLocalisedCanonical(market, pathname);

    return generateSearchResultItemMicrodata(item, logo, url);
  });

  const faqPageData = generateFaqPageMicrodata(faqs);

  const webPageData = generateWebPageMicrodata(title, description);

  const itemListData = generateItemListMicrodata(
    title,
    description,
    list.map((item) => getLocalisedCanonical(market, getSupplierUrl(item))),
  );

  const breadcrumbsData = generateBreadcrumbListMicrodata(
    getBreadcrumbsLocation({
      locationData,
      market,
      slug: actualCategory,
    }).map((item) => ({
      ...item,
      path: getLocalisedCanonical(market, item.path),
    })),
  );

  return (
    <>
      {webPageData && <LdJsonScript data={webPageData} />}
      {searchItemsData.length > 0 && <LdJsonScript data={searchItemsData} />}
      {faqPageData && <LdJsonScript data={faqPageData} />}
      {breadcrumbsData && <LdJsonScript data={breadcrumbsData} />}
      {itemListData && <LdJsonScript data={itemListData} />}
    </>
  );
};

export default memo(SearchResultsMicrodata);
